import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import AccesparkDetailDialog from "./AccesparkDetailDialog";
import useAccesparkHistory from "../hooks/useAccesparkHistory";
import { cycleDate } from "../../creacionCiclo/model/cycleModel";
import {
  displayAccesparkDate,
  displayAccesparkCreatedAt,
  filterAccesparkCycles,
  isOpeningCycle,
} from "../model/accesparkModel";

const colors = {
  success: ["#e4f3e9", "#217346"],
  warning: ["#fff1d5", "#885515"],
  error: ["#fde5e2", "#b42318"],
  info: ["#eef3f8", "#526579"],
};
const chipStyle = (severity) => ({
  bgcolor: colors[severity][0],
  color: colors[severity][1],
  fontWeight: 600,
});
export default function AccesparkHistory({ refreshFlag, type, system }) {
  const { cycles, loading, error, lastUpdated, loadCycles } = useAccesparkHistory(type);
  const [filters, setFilters] = useState({
    search: "",
    commerce: "",
    start: "",
    end: "",
    result: "",
  });
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    loadCycles();
  }, [loadCycles, refreshFlag]);
  const opening = isOpeningCycle(type);
  const accent = opening ? "#14634e" : "#4657a1";
  const records = cycles;
  const commerces = useMemo(
    () => [...new Set(records.map((row) => row.metadata?.commerce).filter(Boolean))].sort(),
    [records]
  );
  const invalidRange = filters.start && filters.end && filters.start > filters.end;
  const filtered = useMemo(() => filterAccesparkCycles(records, filters), [records, filters]);
  const stats = useMemo(
    () =>
      filtered.reduce(
        (acc, row) => {
          acc[row.result.severity] += 1;
          return acc;
        },
        { success: 0, warning: 0, error: 0, info: 0 }
      ),
    [filtered]
  );
  const update = (name) => (event) => {
    setFilters((prev) => ({ ...prev, [name]: event.target.value }));
    setPage(0);
  };
  const currentPage = Math.min(page, Math.max(0, Math.ceil(filtered.length / size) - 1));
  return (
    <Box
      sx={{
        "& .MuiButton-outlined": { color: "#185a75", borderColor: "#90adbd" },
        "& .MuiTextField-root .MuiInputBase-root": { minHeight: 40 },
        "& .MuiSelect-icon": { display: "block" },
      }}
    >
      <Box
        sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2 }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, color: "#183b50" }}>
            Historial de {opening ? "aperturas" : "cierres"}{" "}
            {system === "ezytec" ? "Ezytec" : "Accespark"}
          </Typography>
          <Typography sx={{ fontSize: ".75rem", color: "#526579" }}>
            Última consulta: {cycleDate(lastUpdated)} · Colombia (UTC−5)
          </Typography>
        </Box>
        <Button variant="outlined" onClick={loadCycles} disabled={loading}>
          {loading ? "Actualizando..." : "Actualizar"}
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          {cycles.length ? " Se conservan los registros de la última consulta." : ""}
        </Alert>
      )}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          ["Intentos filtrados", filtered.length, "info"],
          [opening ? "Aperturas exitosas" : "Completados", stats.success, "success"],
          ["Rechazados", stats.warning, "warning"],
          ["Errores técnicos", stats.error, "error"],
        ].map(([label, count, severity]) => (
          <Grid item xs={6} md={3} key={label}>
            <Box sx={{ ...chipStyle(severity), p: 2, borderRadius: 2 }}>
              <Typography sx={{ fontSize: ".75rem", color: "inherit" }}>{label}</Typography>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "inherit" }}>
                {count.toLocaleString("es-CO")}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <TextField
            size="small"
            fullWidth
            label="Buscar placa, usuario, ID o mensaje"
            value={filters.search}
            onChange={update("search")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            size="small"
            select
            fullWidth
            label="Comercio"
            value={filters.commerce}
            onChange={update("commerce")}
          >
            <MenuItem value="">Todos</MenuItem>
            {commerces.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            size="small"
            select
            fullWidth
            label="Resultado"
            value={filters.result}
            onChange={update("result")}
          >
            {[
              ["", "Todos"],
              ["success", opening ? "Aperturas exitosas" : "Completados"],
              ["warning", "Rechazados"],
              ["error", "Errores técnicos"],
              ["info", "Sin respuesta"],
            ].map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            size="small"
            fullWidth
            type="date"
            label={`${opening ? "Entrada" : "Salida"} desde · Colombia`}
            InputLabelProps={{ shrink: true }}
            value={filters.start}
            onChange={update("start")}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            size="small"
            fullWidth
            type="date"
            label={`${opening ? "Entrada" : "Salida"} hasta · Colombia`}
            InputLabelProps={{ shrink: true }}
            value={filters.end}
            onChange={update("end")}
          />
        </Grid>
      </Grid>
      {invalidRange && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          La fecha inicial debe ser anterior o igual a la final.
        </Alert>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography sx={{ fontSize: ".75rem", color: "#526579" }}>
          {filtered.length} de {records.length} intentos · Cada fila corresponde a una solicitud, no
          a un ciclo único.
        </Typography>
        <Button
          size="small"
          onClick={() => {
            setFilters({ search: "", commerce: "", start: "", end: "", result: "" });
            setPage(0);
          }}
        >
          Limpiar filtros
        </Button>
      </Box>
      {loading && !records.length ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: "65vh", border: "1px solid #e1e8ef", borderRadius: 2 }}>
            <Table stickyHeader size="small" sx={{ minWidth: 1050 }}>
              <TableHead sx={{ display: "table-header-group", p: 0 }}>
                <TableRow>
                  {[
                    "ID",
                    "Comercio / Placa",
                    opening ? "Fecha de entrada · Colombia" : "Fecha de salida · Colombia",
                    "Usuario",
                    "IP",
                    "Resultado",
                    "Registro · Colombia",
                    "Detalle",
                  ].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        bgcolor: accent,
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: ".72rem",
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.slice(currentPage * size, (currentPage + 1) * size).map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ "&:nth-of-type(even)": { bgcolor: "#f7f9fc" } }}
                  >
                    <TableCell sx={{ fontWeight: 700 }}>#{row.id}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: ".78rem", fontWeight: 600 }}>
                        {row.metadata?.commerce || "Sin comercio"}
                      </Typography>
                      <Chip
                        size="small"
                        label={row.metadata?.plate || "Sin placa"}
                        sx={{ mt: 0.5, bgcolor: "#e8eff7", color: "#183b50", fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: ".75rem", whiteSpace: "nowrap" }}>
                      {displayAccesparkDate(row.eventAt)}
                    </TableCell>
                    <TableCell>
                      {row.metadata?.userId ?? row.user_id ?? "Sin información"}
                    </TableCell>
                    <TableCell sx={{ fontSize: ".75rem" }}>{row.ip || "Sin información"}</TableCell>
                    <TableCell>
                      <Tooltip title={row.result.message} arrow>
                        <Box sx={{ minWidth: 180, maxWidth: 300 }}>
                          <Chip
                            size="small"
                            label={`${row.result.label}${
                              row.result.code !== null ? ` · ${row.result.code}` : ""
                            }`}
                            sx={chipStyle(row.result.severity)}
                          />
                          <Typography
                            sx={{
                              mt: 0.75,
                              fontSize: ".72rem",
                              color: "#526579",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {row.result.message}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontSize: ".75rem", whiteSpace: "nowrap" }}>
                      {displayAccesparkCreatedAt(row.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => setSelected(row)}>
                        Ver detalle
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!filtered.length && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: "center", p: 4 }}>
                      No hay intentos para esta consulta.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filtered.length}
            page={currentPage}
            rowsPerPage={size}
            rowsPerPageOptions={[10, 25, 50, 100, 200]}
            onPageChange={(_, next) => setPage(next)}
            onRowsPerPageChange={(event) => {
              setSize(Number(event.target.value));
              setPage(0);
            }}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </>
      )}
      <AccesparkDetailDialog cycle={selected} onClose={() => setSelected(null)} />
    </Box>
  );
}
AccesparkHistory.propTypes = {
  refreshFlag: PropTypes.bool,
  type: PropTypes.oneOf([2, 3, 4, 5]).isRequired,
  system: PropTypes.oneOf(["accespark", "ezytec"]),
};
AccesparkHistory.defaultProps = { refreshFlag: false, system: "accespark" };
